import { makeStyles } from "tss-react/mui";
import { MiStackedTypography } from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { formatDate } from "@miplatform/utils";
import { Box, Paper, Tooltip, Typography } from "@mui/material";

const quickBooksStatusConfig = {
  NotSynced: {
    color: "#BDBDBD",
    label: "Invoices not synced",
    statusLabel: "Not Synced",
    chipLabelColor: "#212121",
    chipBorderColor: "#757575",
    chipBackGroundColor: "#BDBDBD",
  },
  Failed: {
    color: "#D32F2F",
    label: "Invoices failed to sync",
    statusLabel: "Failed Sync",
    chipLabelColor: "#fff",
    chipBorderColor: "#D32F2F",
    chipBackGroundColor: "#F44336",
  },
  Partial: {
    color: "#EF6C00",
    label: "Invoices partially synced",
    statusLabel: "Partially Synced",
    chipLabelColor: "#fff",
    chipBorderColor: "#EF6C00",
    chipBackGroundColor: "#EF6C00",
  },
  Synced: {
    color: "#388E3C",
    label: "Invoices fully synced",
    statusLabel: "Synced",
    chipLabelColor: "#fff",
    chipBorderColor: "#388E3C",
    chipBackGroundColor: "#4CAF50",
  },
};

const QuickBooksIconTooltip = ({
  status = "NotSynced",
  syncedDate,
  invoices,
}) => {
  const { classes } = useStyles({
    bgColor: quickBooksStatusConfig[status]?.color || "#BDBDBD",
  });

  return (
    <Paper className={classes.paper}>
      <Box className={classes.header}>
        <Typography className={classes.headerText}>QuickBooks Sync</Typography>
        <Typography className={classes.headerText}>
          <Typography component="span" fontWeight="bold" fontSize={12}>
            {quickBooksStatusConfig[status]?.statusLabel || "Unknown Status"}
          </Typography>
        </Typography>
      </Box>
      <Box bgcolor="#F5F5F5" padding="4px 8px">
        <MiStackedTypography
          title="Synced On"
          description={syncedDate}
          descriptionProps={{
            fontWeight: "600",
            maxWidth: 120,
          }}
        />
      </Box>
      <Box className={classes.topContent}>
        {invoices && (
          <Typography variant="body2" color="grey.medium" fontWeight={600}>
            Invoices
          </Typography>
        )}

        {invoices?.length > 0 ? (
          <Box display="flex" flexDirection="column" gap={1}>
            {invoices.map((invoice) => (
              <Box
                key={invoice.id}
                bgcolor="grey.mediumLight"
                p={1.5}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                borderRadius={1}
                border="1px solid"
                borderColor="grey.300"
                sx={{
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: "grey.100",
                    borderColor: "grey.400",
                  },
                }}
              >
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography
                    variant="body2"
                    color="black.main"
                    fontWeight={500}
                  >
                    {invoice.name}
                  </Typography>
                  <Typography variant="body2" color="grey.600" fontSize={13}>
                    {invoice.invoiceNumber}
                  </Typography>
                </Box>

                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap={1}
                >
                  {invoice?.voidDate && (
                    <Box
                      bgcolor="error.main"
                      px={1.5}
                      py={0.5}
                      borderRadius={1}
                      border="1px solid"
                      borderColor="error.dark"
                    >
                      <Typography
                        color="white"
                        fontSize={11}
                        fontWeight={500}
                        whiteSpace="nowrap"
                      >
                        Voided:{" "}
                        {formatDate(
                          invoice.voidDate,
                          DATE_TIME_FORMATS.FORMAT_MM_DD_YY
                        )}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    bgcolor={
                      quickBooksStatusConfig[
                        invoice.quickBooksAuditStatus || "NotSynced"
                      ]?.chipBackGroundColor
                    }
                    px={1.5}
                    py={0.5}
                    borderRadius={1}
                    border="1px solid"
                    borderColor={
                      quickBooksStatusConfig[
                        invoice.quickBooksAuditStatus || "NotSynced"
                      ]?.chipBorderColor
                    }
                  >
                    <Typography
                      fontSize={11}
                      fontWeight={500}
                      color={
                        quickBooksStatusConfig[
                          invoice.quickBooksAuditStatus || "NotSynced"
                        ]?.chipLabelColor
                      }
                      whiteSpace="nowrap"
                    >
                      {invoice.quickBooksAuditStatus || "Not Synced"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          invoices && (
            <Box
              p={2}
              textAlign="center"
              bgcolor="grey.50"
              borderRadius={1}
              border="1px dashed"
              borderColor="grey.300"
            >
              <Typography variant="body2" color="grey.600">
                No invoices available
              </Typography>
            </Box>
          )
        )}
      </Box>
    </Paper>
  );
};

export const QuickBooksIcon = ({ order = {}, invoices, size = 16 }) => {
  const { id, quickBooksStatus, invoiceFinalizedOnDate } = order;
  return (
    <Tooltip
      title={
        <QuickBooksIconTooltip
          status={quickBooksStatus}
          syncedDate={formatDate(invoiceFinalizedOnDate, "MM/DD/YYYY")}
          invoices={invoices}
          orderId={id}
        />
      }
      slotProps={{
        popper: {
          placement: "right",
          modifiers: [
            {
              name: "offset",
              options: { offset: [0, -18] },
            },
          ],
          sx: { p: 0, m: 0 },
        },
        tooltip: { sx: { p: 0, m: 0 } },
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.00098 0C12.419 0.000263875 16.001 3.58188 16.001 8C16.001 12.4181 12.419 15.9997 8.00098 16C3.5827 16 0 12.4183 0 8C0 3.58172 3.5827 0 8.00098 0ZM5.32617 4.83789C4.22239 4.82915 2.30735 5.72256 2.41406 8.05566C2.24714 8.78328 3.04189 11.3956 5.8584 11.2471V10.0508C5.06503 10.002 3.77538 9.76702 3.59766 8.24219C3.44858 6.96198 3.99692 5.93391 6.5498 6.06152V12.6572H7.67969V4.83789H5.32617ZM8.41504 3.34473V11.1641H10.7686C11.8723 11.1728 13.7874 10.2794 13.6807 7.94629C13.8476 7.21868 13.0528 4.60634 10.2363 4.75488V5.95117C11.0297 5.99993 12.3193 6.23493 12.4971 7.75977C12.6461 9.03997 12.0978 10.068 9.54492 9.94043V3.34473H8.41504Z"
          fill={quickBooksStatusConfig[quickBooksStatus]?.color || "#BDBDBD"}
        />
      </svg>
    </Tooltip>
  );
};

const useStyles = makeStyles()((theme, { bgColor }) => ({
  paper: {
    position: "relative",
    borderRadius: theme.spacing(1.5),
    minWidth: 288,
    maxWidth: 450,
    border: `1px solid ${bgColor}`,
  },
  header: {
    backgroundColor: bgColor,
    padding: theme.spacing(1, 2, 1, 2),
    display: "flex",
    justifyContent: "space-between",
    maxWidth: "100%",
  },
  headerText: {
    fontSize: theme.fontSize.small,
    color: theme.palette.primary.contrastText,
  },
  topContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));
